import { useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { ALL_VARIANTS, Variant, VARIANT_LABELS } from "../models/Page";
import { buildVariantHref, pagesAvailableForVariant, VARIANT_PARAM } from "../utils/Variant";

interface IProps {
    open: boolean;
    onClose: () => void;
}

export default function ShareVariantDialog({ open, onClose }: IProps) {
    const [variant, setVariant] = useState<Variant>(ALL_VARIANTS[0]);
    const [copied, setCopied] = useState(false);

    const pages = useMemo(() => pagesAvailableForVariant(variant), [variant]);

    const shareUrl = useMemo(() => {
        const origin = typeof window !== "undefined" ? window.location.origin : "";
        const path = buildVariantHref("/", variant);
        return `${origin}${path}`;
    }, [variant]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // ignore – fall back to manual copy via the visible field
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Share a variant of the docs</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Pick the audience. The shared link hides unrelated sections from the sidebar; deep links to hidden pages still work.
                </DialogContentText>
                <Stack spacing={2}>
                    <TextField
                        select
                        label="Variant"
                        value={variant}
                        onChange={(e) => setVariant(e.target.value as Variant)}
                        fullWidth
                    >
                        {ALL_VARIANTS.map((v) => (
                            <MenuItem key={v} value={v}>
                                {VARIANT_LABELS[v]}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Sections visible in sidebar (Home is always shown)
                        </Typography>
                        <List dense disablePadding>
                            {pages.map((p) => (
                                <ListItem key={p.href} disableGutters sx={{ py: 0 }}>
                                    <ListItemText primary={p.title} />
                                </ListItem>
                            ))}
                            {pages.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No pages are tagged for this variant yet. Add a <code>tags</code> entry on the page in <code>src/models/Page.tsx</code>.
                                </Typography>
                            )}
                        </List>
                    </Box>

                    <TextField
                        label="Shareable link"
                        value={shareUrl}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleCopy} edge="end" aria-label="Copy link">
                                        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        Tip: append <code>?{VARIANT_PARAM}={variant}</code> to any docs URL to share a deep link in this variant.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
